import { rewriteGreApiUrl } from '../core/exams/gre/api/greApiPaths';
import { resolveApiBaseUrl } from './apiBaseUrl';
import {
  AUTH_LOGOUT_URL,
  AUTH_REFRESH_URL,
  getAccessTokenForApiHeaders,
  refreshSessionIfNeeded,
} from './authSession';

const API_BASE_URL = resolveApiBaseUrl();

const AUTH_ENDPOINTS_NO_RETRY = [AUTH_REFRESH_URL, AUTH_LOGOUT_URL];

const isApiRequest = (url) =>
  typeof url === 'string' &&
  (url.startsWith('/api/') || url.startsWith(`${API_BASE_URL}/api/`));

const shouldRetryOn401 = (url) =>
  isApiRequest(url) &&
  !AUTH_ENDPOINTS_NO_RETRY.some((endpoint) => url.startsWith(endpoint));

const resolveFetchTarget = (input, init) => {
  const requestUrl =
    typeof input === 'string' || input instanceof URL
      ? String(input)
      : input?.url;

  if (!requestUrl) {
    return { input, init };
  }

  const rewrittenUrl = rewriteGreApiUrl(requestUrl);

  if (rewrittenUrl === requestUrl) {
    return { input, init };
  }

  if (input instanceof Request) {
    return {
      input: new Request(rewrittenUrl, input),
      init,
    };
  }

  return {
    input: rewrittenUrl,
    init,
  };
};

const buildFetchInit = (input, init) => {
  if (input instanceof Request) {
    return {
      credentials: init?.credentials || 'include',
      ...init,
    };
  }

  return {
    credentials: 'include',
    ...init,
  };
};

const retryRequestWithFreshToken = (input, init) => {
  const token = getAccessTokenForApiHeaders();
  const retryInit = {
    ...buildFetchInit(input, init),
    _liwazaAuthRetry: true,
  };

  if (input instanceof Request) {
    const headers = new Headers(input.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.delete('Authorization');
    }

    const nextRequest = new Request(input, {
      ...retryInit,
      headers,
    });
    return nextRequest;
  }

  const headers = new Headers(retryInit.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    headers.delete('Authorization');
  }

  return {
    input,
    init: {
      ...retryInit,
      headers,
    },
  };
};

if (typeof window !== 'undefined' && !window.__liwazaFetchWrapped) {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const { input: resolvedInput, init: resolvedInit } = resolveFetchTarget(
      input,
      init
    );
    const requestUrl =
      typeof resolvedInput === 'string' || resolvedInput instanceof URL
        ? String(resolvedInput)
        : resolvedInput?.url;

    if (!isApiRequest(requestUrl)) {
      return nativeFetch(resolvedInput, resolvedInit);
    }

    const fetchInit = buildFetchInit(resolvedInput, resolvedInit);
    const initialResponse = await nativeFetch(
      resolvedInput instanceof Request
        ? new Request(resolvedInput, fetchInit)
        : resolvedInput,
      resolvedInput instanceof Request ? undefined : fetchInit
    );

    if (
      initialResponse.status !== 401 ||
      init?._liwazaAuthRetry ||
      !shouldRetryOn401(requestUrl)
    ) {
      return initialResponse;
    }

    const refreshResult = await refreshSessionIfNeeded({
      force: true,
      broadcastOnSuccess: true,
    });

    if (!refreshResult?.ok) {
      return initialResponse;
    }

    const retryPayload = retryRequestWithFreshToken(resolvedInput, resolvedInit);
    if (retryPayload instanceof Request) {
      return nativeFetch(retryPayload);
    }

    return nativeFetch(retryPayload.input, retryPayload.init);
  };

  window.__liwazaFetchWrapped = true;
}
